// /composables/useAlert.js
import { inject } from 'vue'
import { AlertContext } from '@cnd_fe/alert' 
import { MESSAGE_CONSTANTS } from '@/constants/message'

const confirmTitle = (type) => {
    switch (type) {
        case 'delete': return '삭제 확인'
        case 'insert': return '등록 확인'
        case 'update': return '수정 확인'
        case 'save': return '저장 확인'
        case 'info': return '알림'
        default: return '확인'
    }
}

const confirmContent = (type, target = '항목') => {
    switch (type) {
        case 'delete':
            return MESSAGE_CONSTANTS.COMMON.DELETE(`해당 ${target}`)
        case 'insert':
            return MESSAGE_CONSTANTS.COMMON.INSERT(target)
        case 'update':
            return MESSAGE_CONSTANTS.COMMON.UPDATE(target)
        case 'save':
            return MESSAGE_CONSTANTS.COMMON.SAVE(target)
        case 'info':
            return target
        default:
            return MESSAGE_CONSTANTS.COMMON.SAVE(target)
    }
}


const successTitle = (type) => {
    switch (type) {
        case 'delete': return '삭제 완료'
        case 'insert': return '등록 완료'
        case 'update': return '수정 완료'
        case 'file-delete': return '파일 삭제 완료'
        default: return '처리 완료'
    }
}

const successMessage = (type) => {
    switch (type) {
        case 'delete': return MESSAGE_CONSTANTS.SUCCESS.DELETE
        case 'insert': return MESSAGE_CONSTANTS.SUCCESS.INSERT
        case 'update': return MESSAGE_CONSTANTS.SUCCESS.UPDATE
        case 'file-delete': return MESSAGE_CONSTANTS.SUCCESS.FILE_DELETE
        default: return MESSAGE_CONSTANTS.SUCCESS.SAVE
    }
}

export const useAlert = () => {
    const ctx = inject(AlertContext, null)
    if (!ctx) throw new Error('useAlert() must be used within <AlertProvider>.')

    // 확인 모달 (Promise<boolean>)
    const confirm = (type, target = '항목', options = {}) => {
        return ctx.confirm({
            type, 
            variant: type === 'delete' ? 'danger' : 'primary', 
            title: confirmTitle(type),
            content: confirmContent(type, target),
            confirmText: type === 'delete' ? '삭제' : '확인',
            cancelText: '취소',
            ...options
        })
    }

    // 성공 모달/토스트
    const success = (type = 'save', options = {}) => {
        const title = options.title ?? successTitle(type)
        const content = options.content ?? successMessage(type)
        return ctx.success({
            title,
            message: content,
            content, // 혹시 content를 쓰는 구현도 대비
            ...options
        })
    }

    // 오류 토스트 (기본 메시지 + 커스텀 가능)
    const error = (message = '처리 중 오류가 발생했습니다.', options = {}) => {
        const title = options.title ?? '오류'
        const content = options.content ?? message

        return ctx.error({
            title,
            message: content,
            content,
            ...options
        })
    }

    // window.alert 대체 — 확인 버튼만 있는 모달
    const showAlert = (message, options = {}) => {
        return ctx.confirm({
            title: options.title ?? '알림',
            content: message,
            confirmText: '확인',
            cancelText: '',
            preventOutsideClose: options.preventOutsideClose ?? true,
            ...options
        })
    }

    return {
        confirm,
        success,
        error,
        showAlert,
        // 필요하면 원본도 노출
        info: ctx.info,
        warning: ctx.warning,
        isBusy: ctx.isBusy
    }
}
